// Single source of truth for the termination & violation rule-set that a
// landlord must attach to a property before it can go for admin approval.
//
// The form behind this used to be ~20 empty fields spread over 10 sections,
// and every landlord had to fill all of them from scratch for every property.
// Almost all of them ended up picking the same handful of combinations, so the
// presets below encode those combinations once. The full form is still there
// (Customize mode) — the presets just mean nobody has to start from blank.

export const TERMINATION_REASONS = [
  "Non Payment",
  "Illegal Activities",
  "Property Damage",
  "Agreement Violation",
  "Criminal Activity",
  "Utility Bills Not Paid",
  "Other",
];

export const NOTICE_PERIOD_OPTIONS = ["7 Days", "15 Days", "30 Days", "60 Days"];
export const TENANT_PENALTY_OPTIONS = [
  "No Penalty",
  "One Month Rent",
  "Two Month Rent",
  "Custom",
];
export const COMPENSATION_OPTIONS = ["None", "One Month Rent", "Custom"];
export const REFUND_WITHIN_OPTIONS = ["3 Days", "7 Days", "15 Days"];
export const DAMAGE_RECOVERY_OPTIONS = [
  { value: "Security Deposit", label: "Security Deposit" },
  { value: "Tenant Pays", label: "Tenant Pays Separately" },
  { value: "Owner Absorbs", label: "Owner Absorbs" },
];
export const DISPUTE_METHOD_OPTIONS = [
  { value: "Discussion", label: "Discussion" },
  { value: "Mediation", label: "Mediation" },
  { value: "Court", label: "Legal Action" },
];

// Every key the PropertySetting schema accepts, minus the identity fields
// (_id/userId/properties/timestamps). Used to copy an existing setting without
// dragging its Mongo metadata into a create payload.
export const RULE_FIELDS = [
  "tenantNoticePeriod",
  "tenantPenalty",
  "tenantCustomPenalty",
  "deductSecurity",
  "deductAdvance",
  "tenantPaysSeparately",
  "ownerNoticePeriod",
  "ownerCompensation",
  "ownerCustomCompensation",
  "refundPolicy",
  "terminationReasons",
  "refundWithin",
  "inspectionRequired",
  "utilityClearance",
  "keyReturn",
  "damageRecovery",
  "allowMutualTermination",
  "mutualPenalty",
  "disputeMethod",
  "additionalRules",
];

export const EMPTY_RULES = {
  tenantNoticePeriod: "",
  tenantPenalty: "",
  tenantCustomPenalty: "",
  deductSecurity: true,
  deductAdvance: false,
  tenantPaysSeparately: false,
  ownerNoticePeriod: "",
  ownerCompensation: "",
  ownerCustomCompensation: "",
  refundPolicy: "full",
  terminationReasons: [],
  refundWithin: "",
  inspectionRequired: true,
  utilityClearance: true,
  keyReturn: true,
  damageRecovery: "",
  allowMutualTermination: true,
  mutualPenalty: "",
  disputeMethod: "",
  additionalRules: "",
};

export const SETTING_PRESETS = [
  {
    key: "standard",
    label: "Standard",
    tagline: "Recommended",
    description:
      "Balanced terms that match the usual Karachi rental agreement. Good default for most flats.",
    accent: "#667eea",
    values: {
      ...EMPTY_RULES,
      tenantNoticePeriod: "30 Days",
      tenantPenalty: "One Month Rent",
      deductSecurity: true,
      ownerNoticePeriod: "30 Days",
      ownerCompensation: "One Month Rent",
      refundPolicy: "full",
      terminationReasons: [
        "Non Payment",
        "Illegal Activities",
        "Property Damage",
        "Criminal Activity",
      ],
      refundWithin: "7 Days",
      damageRecovery: "Security Deposit",
      allowMutualTermination: true,
      mutualPenalty: "None",
      disputeMethod: "Mediation",
    },
  },
  {
    key: "strict",
    label: "Strict",
    tagline: "Owner protected",
    description:
      "Longer notice, heavier penalties and every violation reason enabled. Use for high-value or previously damaged properties.",
    accent: "#dc2626",
    values: {
      ...EMPTY_RULES,
      tenantNoticePeriod: "60 Days",
      tenantPenalty: "Two Month Rent",
      deductSecurity: true,
      deductAdvance: true,
      ownerNoticePeriod: "15 Days",
      ownerCompensation: "None",
      refundPolicy: "dues",
      terminationReasons: TERMINATION_REASONS.filter((r) => r !== "Other"),
      refundWithin: "15 Days",
      damageRecovery: "Tenant Pays",
      allowMutualTermination: false,
      mutualPenalty: "One Month Rent",
      disputeMethod: "Court",
    },
  },
  {
    key: "flexible",
    label: "Flexible",
    tagline: "Fills faster",
    description:
      "Short notice and no exit penalty. Attracts tenants quickly when a property has been vacant for a while.",
    accent: "#16a34a",
    values: {
      ...EMPTY_RULES,
      tenantNoticePeriod: "15 Days",
      tenantPenalty: "No Penalty",
      deductSecurity: false,
      ownerNoticePeriod: "60 Days",
      ownerCompensation: "One Month Rent",
      refundPolicy: "full",
      terminationReasons: [
        "Non Payment",
        "Illegal Activities",
        "Criminal Activity",
      ],
      refundWithin: "3 Days",
      damageRecovery: "Security Deposit",
      allowMutualTermination: true,
      mutualPenalty: "None",
      disputeMethod: "Discussion",
    },
  },
];

export const getPreset = (key) => SETTING_PRESETS.find((p) => p.key === key);

// Copies only the rule keys off a saved setting, so a "start from an existing
// setting" action never carries _id/userId/__v into a create payload.
export function pickRuleFields(source = {}) {
  const out = { ...EMPTY_RULES };

  RULE_FIELDS.forEach((field) => {
    if (source[field] !== undefined && source[field] !== null) {
      out[field] = source[field];
    }
  });

  out.terminationReasons = Array.isArray(out.terminationReasons)
    ? out.terminationReasons
    : [];

  return out;
}

const sameReasons = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

// Which preset (if any) the current form still matches. Lets the UI keep the
// preset card highlighted until the landlord actually changes a rule, at which
// point it flips to "Custom" instead of lying about what is selected.
export function matchPreset(rules) {
  const found = SETTING_PRESETS.find((preset) =>
    RULE_FIELDS.every((field) => {
      if (field === "terminationReasons") {
        return sameReasons(rules[field], preset.values[field]);
      }
      return rules[field] === preset.values[field];
    })
  );

  return found ? found.key : "custom";
}

const penaltyText = (choice, custom) => {
  if (!choice) return null;
  if (choice === "No Penalty" || choice === "None") return "no penalty";
  if (choice === "Custom") {
    return custom ? `Rs ${custom} penalty` : "a custom penalty";
  }
  return `${choice.toLowerCase()} as penalty`;
};

const join = (parts) => parts.filter(Boolean).join(" • ");

// Plain-English read-out of the rule set, so a landlord can confirm what they
// are about to apply without expanding all nine sections.
export function summarizeSetting(rules) {
  return [
    {
      label: "Tenant leaves early",
      text:
        join([
          rules.tenantNoticePeriod && `${rules.tenantNoticePeriod} notice`,
          penaltyText(rules.tenantPenalty, rules.tenantCustomPenalty),
          rules.deductSecurity && "security deposit adjusted",
          rules.deductAdvance && "advance rent adjusted",
          rules.tenantPaysSeparately && "tenant pays the rest separately",
        ]) || "Not set",
    },
    {
      label: "Owner ends agreement",
      text:
        join([
          rules.ownerNoticePeriod && `${rules.ownerNoticePeriod} notice`,
          penaltyText(rules.ownerCompensation, rules.ownerCustomCompensation) &&
            (rules.ownerCompensation === "None"
              ? "no compensation"
              : `${penaltyText(
                  rules.ownerCompensation,
                  rules.ownerCustomCompensation
                ).replace("penalty", "compensation")}`),
          rules.refundPolicy === "dues"
            ? "outstanding dues deducted"
            : "full refund",
        ]) || "Not set",
    },
    {
      label: "Immediate termination",
      text: rules.terminationReasons?.length
        ? `${rules.terminationReasons.length} reason${
            rules.terminationReasons.length > 1 ? "s" : ""
          }: ${rules.terminationReasons.join(", ")}`
        : "No reasons selected",
    },
    {
      label: "Security deposit",
      text:
        join([
          rules.refundWithin && `refunded within ${rules.refundWithin}`,
          rules.inspectionRequired && "after inspection",
          rules.utilityClearance && "utility clearance",
          rules.keyReturn && "key return",
        ]) || "Not set",
    },
    {
      label: "Damage & disputes",
      text:
        join([
          rules.damageRecovery &&
            `damage via ${
              DAMAGE_RECOVERY_OPTIONS.find(
                (o) => o.value === rules.damageRecovery
              )?.label || rules.damageRecovery
            }`,
          rules.allowMutualTermination
            ? "mutual termination allowed"
            : "no mutual termination",
          rules.disputeMethod &&
            `disputes via ${
              DISPUTE_METHOD_OPTIONS.find((o) => o.value === rules.disputeMethod)
                ?.label || rules.disputeMethod
            }`,
        ]) || "Not set",
    },
  ];
}

// The handful of rules that genuinely need a decision before a setting is
// usable. Everything else has a safe default, so these are the only things the
// form blocks on.
export function validateRules(rules) {
  const errors = {};

  if (!rules.tenantNoticePeriod) {
    errors.tenantNoticePeriod = "Select how much notice a tenant must give";
  }
  if (!rules.tenantPenalty) {
    errors.tenantPenalty = "Select the early-exit penalty";
  }
  if (rules.tenantPenalty === "Custom" && !rules.tenantCustomPenalty) {
    errors.tenantCustomPenalty = "Enter the custom penalty amount";
  }
  if (!rules.ownerNoticePeriod) {
    errors.ownerNoticePeriod = "Select how much notice you must give";
  }
  if (rules.ownerCompensation === "Custom" && !rules.ownerCustomCompensation) {
    errors.ownerCustomCompensation = "Enter the custom compensation amount";
  }
  if (!rules.terminationReasons?.length) {
    errors.terminationReasons = "Select at least one immediate-termination reason";
  }
  if (!rules.refundWithin) {
    errors.refundWithin = "Select the security-deposit refund window";
  }
  if (!rules.damageRecovery) {
    errors.damageRecovery = "Select how damage charges are recovered";
  }
  if (rules.mutualPenalty === "Custom" && !rules.additionalRules) {
    errors.mutualPenalty = "Describe the custom mutual-termination penalty in Additional Rules";
  }
  if (!rules.disputeMethod) {
    errors.disputeMethod = "Select a dispute-resolution method";
  }

  return errors;
}
